import { apiGiftCardsService, apiPublicService } from '@api';
import { modalIds } from '@const/modals';
import { useApiMutation } from '@hooks/api/useApiMutation';
import useModalContext from '@hooks/context/useModalContext';
import { useUserContext } from '@hooks/context/useUserContext';
import { useLocales } from '@hooks/helpers/useLocales';
import { useHelpCenterModals } from '@hooks/modals/useHelpCenterModals';
import { EGiftCardStatus, TGiftCard, TGiftCardListItem } from '@xeppt/xeppt-sdk/types';

interface IProps {
    refetchGiftCards?: () => void;
}

export const useGiftCardsModals = ({ refetchGiftCards }: IProps) => {
    const { onClose, onOpen, changeModalData, handleChangeLoading, changeModalState } =
        useModalContext();
    const { requestErrorLocale, requestSuccessLocale } = useLocales();
    const { refetchAccount } = useUserContext();
    const { handleOpenSubmitRequestModal } = useHelpCenterModals();

    const { handleRequest: handleGetGiftCardById } = useApiMutation({
        method: (id?: string) => {
            return apiGiftCardsService.getGiftCardById(id || '');
        },
        onSuccess: (data) => {
            changeModalData({ giftCard: data, refetchGiftCards });
            onOpen({
                modalId: modalIds.VIEW_GIFT_CARD
            });
        }
    });

    const { handleRequest: handleRedeemGiftCardById } = useApiMutation({
        method: (id?: string) => {
            return apiPublicService.getPublicGiftCardById(id || '');
        },
        onSuccess: (giftCard, request) => {
            changeModalData({ giftCard: giftCard });
            onOpen({
                modalId: modalIds.REDEEM_GIFT_CARD,
                onSubmit: (data: { amount: number; reference: string }) => {
                    handleChangeLoading(true);
                    apiGiftCardsService
                        .redeemGiftCard(request || '', {
                            amount: Number(data.amount),
                            reference: data.reference
                        })
                        .then(() => {
                            onClose();
                            refetchAccount();
                            requestSuccessLocale('gift_card_redeemed');
                            refetchGiftCards && refetchGiftCards();
                        })
                        .catch(requestErrorLocale)
                        .finally(() => handleChangeLoading(false));
                }
            });
        }
    });

    // const { handleRequest: handleCancelGiftCards } = useApiMutation({});

    const handleOpenViewGiftCardModal = (id: string) => {
        handleGetGiftCardById(id);
    };

    const handleOpenScanGiftCard = () => {
        changeModalData({ refetchGiftCards });
        onOpen({
            modalId: modalIds.SCAN_GIFT_CARD_QR
        });
    };

    const handleOpenAddGiftCardDescription = (id: string, isEdit: boolean, giftCard: TGiftCard) => {
        changeModalData({ id, isEdit, giftCard });
        onOpen({
            modalId: modalIds.ADD_GIFT_CARD_DESCRIPTION,
            onSubmit: (data: { id: string; isEdit?: boolean; description: string }) => {
                handleChangeLoading(true);
                apiGiftCardsService
                    .updateGiftCard(data.id, {
                        description: data.description
                    })
                    .then(() => {
                        onClose();
                        requestSuccessLocale('gift_card_updated');
                        refetchGiftCards && refetchGiftCards();
                    })
                    .catch(requestErrorLocale)
                    .finally(() => {
                        handleChangeLoading(false);
                    });
            }
        });
    };

    const handleOpenRedeemGiftCardModal = (id: string) => {
        handleRedeemGiftCardById(id);
    };

    const handleOpenGiftCardAddRecipientEmail = (
        id: string,
        isEdit: boolean,
        giftCard: TGiftCard | TGiftCardListItem
    ) => {
        setTimeout(() => {
            changeModalState({ confirmLabel: !isEdit ? 'add' : 'save_changes' });
        }, 0);
        changeModalData({
            id,
            isEdit,
            giftCard
        });
        onOpen({
            modalId: modalIds.EMAIL_GIFT_CARD,
            onSubmit: (data: {
                email: string;
                phone_code: string;
                phone_number: string;
                id: string;
            }) => {
                handleChangeLoading(true);
                apiGiftCardsService
                    .updateGiftCard(data.id, {
                        email: data.email || undefined,
                        phone_code: data.phone_code || undefined,
                        phone_number: data.phone_number || undefined
                    })
                    .then(() => {
                        onClose();
                        requestSuccessLocale('gift_card_updated');
                        refetchGiftCards && refetchGiftCards();
                    })
                    .catch(requestErrorLocale)
                    .finally(() => {
                        handleChangeLoading(false);
                    });
            }
        });
    };

    const handleOpenActivateGiftCardModal = (giftCards: (TGiftCard | TGiftCardListItem)[]) => {
        changeModalData({ giftCards: giftCards, refetchGiftCards });
        onOpen({
            modalId: modalIds.ACTIVATE_GIFT_CARD_MODAL,
            onSubmit: () => {
                handleChangeLoading(true);
                apiGiftCardsService
                    .activateGiftCard(giftCards.map((item) => item.id))
                    .then(() => {
                        refetchGiftCards && refetchGiftCards();
                        onClose();
                        requestSuccessLocale(
                            giftCards.length > 1 ? 'gift_cards_activated' : 'gift_card_activated'
                        );
                        refetchAccount();
                        refetchGiftCards && refetchGiftCards();
                    })
                    .catch(requestErrorLocale)
                    .finally(() => {
                        handleChangeLoading(false);
                    });
            }
        });
    };

    const handleCancelGiftCards = (giftCards: (TGiftCard | TGiftCardListItem)[]) => {
        const hasActivatedCards = giftCards.some(
            (card) =>
                card.status === EGiftCardStatus.ACTIVE ||
                card.status === EGiftCardStatus.PARTIALLY_REDEEMED
        );
        changeModalState({
            cancelLabel: giftCards.length > 1 ? 'no_keep_them' : 'no_keep_it'
        });
        changeModalData({ isBulk: giftCards.length > 1, hasActivatedCards });
        onOpen({
            modalId: modalIds.CANCEL_GIFT_CARD,
            onSubmit: () => {
                handleChangeLoading(true);
                apiGiftCardsService
                    .cancelGiftCard(giftCards.map((item) => item.id))
                    .then(() => {
                        refetchGiftCards && refetchGiftCards();
                        onClose();
                        requestSuccessLocale(
                            giftCards.length > 0 ? 'gift_cards_cancelled' : 'gift_card_cancelled'
                        );
                    })
                    .catch(requestErrorLocale)
                    .finally(() => {
                        handleChangeLoading(true);
                    });
            }
        });
    };

    const handleRequestCancelGiftCards = (giftCards: (TGiftCard | TGiftCardListItem)[]) => {
        handleOpenSubmitRequestModal(undefined, undefined, giftCards.join(', '));
    };

    return {
        handleOpenViewGiftCardModal,
        handleOpenRedeemGiftCardModal,
        handleOpenGiftCardAddRecipientEmail,
        handleOpenScanGiftCard,
        handleOpenAddGiftCardDescription,
        handleOpenActivateGiftCardModal,
        handleCancelGiftCards,
        handleRequestCancelGiftCards
    };
};
